import { useEffect, useRef, useState } from "react";
import axios from "axios";
import api from "../../lib/api";
import { toast } from "sonner";
import { PaperPlaneRight, User } from "@phosphor-icons/react";
import { API } from "../../App";

const ROLE_COLORS = {
  admin: "#7C3AED",
  manager: "#002FA7",
  bdm: "#0E7490",
  bde: "#0D9488",
  md: "#B45309",
  hr: "#9333EA",
  marketing_head: "#DB2777",
};

function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true });
}

export default function TaskConversationTab({ taskId, user, onUnreadChange }) {
  const [comments, setComments] = useState([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const scrollerRef = useRef(null);

  const reqParams = {
    requester_user_id: user?.user_id || "",
    requester_name: user?.name || "",
    requester_role: user?.role || "",
  };

  const refresh = async () => {
    if (!taskId) return;
    setLoading(true);
    setError("");
    try {
      const r = await api.get(`${API}/crm/tasks/${taskId}/comments`, { params: reqParams });
      setComments(r.data?.comments || []);
      onUnreadChange && onUnreadChange(r.data?.unread_count || 0);
    } catch (e) {
      if (e?.response?.status === 403) setError("You do not have access to this task's conversation.");
      else setError("Could not load conversation.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // Fire-and-forget mark-read on open
    if (taskId && user?.user_id) {
      api.post(`${API}/crm/tasks/${taskId}/comments/mark-read`, reqParams).then(() => {
        onUnreadChange && onUnreadChange(0);
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  useEffect(() => {
    // auto-scroll to latest on new message
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [comments]);

  const send = async (e) => {
    e?.preventDefault?.();
    const msg = draft.trim();
    if (!msg) return;
    setSending(true);
    try {
      const r = await api.post(`${API}/crm/tasks/${taskId}/comments`, { message: msg, ...reqParams });
      setComments((prev) => [...prev, r.data.comment]);
      setDraft("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  if (error) {
    return <div data-testid="task-conv-error" className="text-sm text-red-600 border border-red-200 bg-red-50 p-3">{error}</div>;
  }

  return (
    <div data-testid="task-conversation-tab" className="flex flex-col h-full -mx-6 -my-5">
      <div ref={scrollerRef} className="flex-1 overflow-y-auto px-6 py-4 space-y-3 min-h-[200px]">
        {loading && <div className="text-sm text-slate-400">Loading…</div>}
        {!loading && comments.length === 0 && (
          <div className="text-sm text-slate-400 italic">No replies yet — start the conversation.</div>
        )}
        {comments.map((c) => {
          const isMe = c.user_id === user?.user_id;
          const color = ROLE_COLORS[(c.user_role || "").toLowerCase()] || "#475569";
          return (
            <div key={c.comment_id} data-testid={`task-comment-${c.comment_id}`} className={`flex flex-col ${isMe ? "items-end" : "items-start"}`}>
              <div className={`max-w-[85%] border ${isMe ? "border-[#002FA7]/30 bg-[#002FA7]/5" : "border-slate-200 bg-white"} px-3 py-2`}>
                <div className="flex items-center gap-2 mb-1">
                  <User size={11} className="text-slate-400" />
                  <span className="text-xs font-bold text-slate-800">{c.user_name || "—"}</span>
                  <span
                    className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5"
                    style={{ backgroundColor: `${color}15`, color }}
                  >
                    {(c.user_role || "").toUpperCase() || "USER"}
                  </span>
                  <span className="text-[10px] text-slate-400 ml-auto">{fmtWhen(c.created_at)}</span>
                </div>
                <div className="text-sm text-slate-700 whitespace-pre-wrap">{c.message}</div>
              </div>
            </div>
          );
        })}
      </div>
      <form onSubmit={send} className="border-t border-slate-200 p-3 bg-slate-50 flex gap-2 items-end" data-testid="task-conv-composer">
        <textarea
          data-testid="task-conv-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(e); }}
          placeholder="Type a reply… (Ctrl/Cmd+Enter to send)"
          rows={2}
          className="flex-1 rounded-none border border-slate-300 focus:border-[#002FA7] p-2 text-sm outline-none bg-white resize-none"
        />
        <button
          data-testid="task-conv-send"
          type="submit"
          disabled={sending || !draft.trim()}
          className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] disabled:bg-slate-300 px-4 py-2 text-xs uppercase tracking-wider font-bold inline-flex items-center gap-1"
        >
          <PaperPlaneRight size={12} weight="bold" />
          {sending ? "Sending…" : "Send"}
        </button>
      </form>
    </div>
  );
}
